import React, { useState, useEffect, useMemo } from 'react';
import {
  Filter,
  X,
  LayoutGrid,
  Users,
  Heart,
  MapPin,
  User,
  CheckCircle2,
  Sparkles,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import type { InfluencerFilters, InfluencerGender } from './types';
import { INITIAL_INFLUENCER_FILTERS } from './constants';

export interface FiltersSidebarProps {
  open: boolean;
  filters: InfluencerFilters;
  setFilters: React.Dispatch<React.SetStateAction<InfluencerFilters>>;
  setShowFilters: (show: boolean) => void;
}

export const FiltersSidebar: React.FC<FiltersSidebarProps> = ({
  open,
  filters,
  setFilters,
  setShowFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<InfluencerFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (localFilters.platform && localFilters.platform !== 'all') count++;
    if (localFilters.gender && localFilters.gender !== 'all') count++;
    if (localFilters.engagement && localFilters.engagement !== 'all') count++;
    if (localFilters.location && localFilters.location !== 'all') count++;
    if (localFilters.tier && localFilters.tier !== 'all') count++;
    if (localFilters.status && localFilters.status !== 'all') count++;
    return count;
  }, [localFilters]);

  const handleApply = () => {
    setFilters(localFilters);
    setShowFilters(false);
  };

  const handleClearAll = () => {
    const cleared: InfluencerFilters = {
      ...INITIAL_INFLUENCER_FILTERS,
    };
    setLocalFilters(cleared);
    setFilters(cleared);
  };

  return (
    <Sheet open={open} onOpenChange={setShowFilters}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[390px] w-full bg-white border-l border-gray-200 flex flex-col h-full shadow-2xl focus:outline-none [&>button:last-child]:hidden"
      >
        {/* Header */}
        <SheetHeader className="p-6 pb-5 border-b border-gray-100 flex flex-row items-center justify-between space-y-0 text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F3E8FF] flex items-center justify-center text-[#A655F8] shrink-0">
              <Filter className="w-5 h-5" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold text-gray-900 leading-tight">Filters</SheetTitle>
              <SheetDescription className="text-xs text-gray-500 mt-0.5">Refine your search and find the perfect creators.</SheetDescription>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(false)}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            aria-label="Close filters"
          >
            <X className="w-4 h-4" />
          </button>
        </SheetHeader>

      {/* Filter items list */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Platform */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#F5EEFD] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
            <LayoutGrid className="w-4 h-4" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-sm font-semibold text-gray-900 block leading-tight">
              Platform
            </label>
            <div className="relative">
              <select
                value={localFilters.platform}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, platform: e.target.value }))
                }
                className="w-full h-10 appearance-none bg-white border border-gray-200 rounded-xl px-3.5 pr-9 text-sm text-gray-700 font-normal focus:outline-none focus:ring-2 focus:ring-[#A655F8]/20 focus:border-[#A655F8] hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="all">All Platforms</option>
                <option value="instagram">Instagram Only</option>
                <option value="youtube">YouTube Only</option>
                <option value="both">Both Platforms</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Gender */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#F5EEFD] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
            <Users className="w-4 h-4" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-sm font-semibold text-gray-900 block leading-tight">
              Gender
            </label>
            <div className="relative">
              <select
                value={localFilters.gender}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    gender: e.target.value as InfluencerGender,
                  }))
                }
                className="w-full h-10 appearance-none bg-white border border-gray-200 rounded-xl px-3.5 pr-9 text-sm text-gray-700 font-normal focus:outline-none focus:ring-2 focus:ring-[#A655F8]/20 focus:border-[#A655F8] hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="all">All Genders</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Engagement Rate */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#F5EEFD] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
            <Heart className="w-4 h-4" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-sm font-semibold text-gray-900 block leading-tight">
              Engagement Rate
            </label>
            <div className="relative">
              <select
                value={localFilters.engagement}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, engagement: e.target.value }))
                }
                className="w-full h-10 appearance-none bg-white border border-gray-200 rounded-xl px-3.5 pr-9 text-sm text-gray-700 font-normal focus:outline-none focus:ring-2 focus:ring-[#A655F8]/20 focus:border-[#A655F8] hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="all">All Rates</option>
                <option value="high">High (5%+)</option>
                <option value="medium">Medium (2-5%)</option>
                <option value="low">Low (&lt;2%)</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#F5EEFD] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
            <MapPin className="w-4 h-4" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-sm font-semibold text-gray-900 block leading-tight">
              Location
            </label>
            <div className="relative">
              <select
                value={localFilters.location}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, location: e.target.value }))
                }
                className="w-full h-10 appearance-none bg-white border border-gray-200 rounded-xl px-3.5 pr-9 text-sm text-gray-700 font-normal focus:outline-none focus:ring-2 focus:ring-[#A655F8]/20 focus:border-[#A655F8] hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="all">All Locations</option>
                <option value="mumbai">Mumbai</option>
                <option value="delhi">Delhi</option>
                <option value="bangalore">Bangalore</option>
                <option value="pune">Pune</option>
                <option value="chennai">Chennai</option>
                <option value="hyderabad">Hyderabad</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Creator Tier / Followers Range */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#F5EEFD] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
            <User className="w-4 h-4" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-sm font-semibold text-gray-900 block leading-tight">
              Creator Tier / Followers Range
            </label>
            <div className="relative">
              <select
                value={localFilters.tier}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, tier: e.target.value }))
                }
                className="w-full h-10 appearance-none bg-white border border-gray-200 rounded-xl px-3.5 pr-9 text-sm text-gray-700 font-normal focus:outline-none focus:ring-2 focus:ring-[#A655F8]/20 focus:border-[#A655F8] hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="all">All Tiers</option>
                <option value="nano">Nano (1K-10K)</option>
                <option value="micro">Micro (10K-100K)</option>
                <option value="macro">Macro (100K-1M)</option>
                <option value="mega">Mega (1M+)</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#F5EEFD] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-sm font-semibold text-gray-900 block leading-tight">
              Status
            </label>
            <div className="relative">
              <select
                value={localFilters.status}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                className="w-full h-10 appearance-none bg-white border border-gray-200 rounded-xl px-3.5 pr-9 text-sm text-gray-700 font-normal focus:outline-none focus:ring-2 focus:ring-[#A655F8]/20 focus:border-[#A655F8] hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="negotiation">In Negotiation</option>
                <option value="archived">Archived</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Pro tip card */}
        <div className="pt-2 border-t border-gray-100 mt-5">
          <div className="bg-[#FAF5FF] rounded-2xl p-4 flex items-start gap-3.5 border border-purple-100/50">
            <div className="w-8 h-8 rounded-full bg-[#F3E8FF] text-[#A655F8] flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="text-xs">
              <p className="font-semibold text-gray-900 leading-tight">Pro tip</p>
              <p className="text-gray-500 mt-1 leading-relaxed">
                Use multiple filters to narrow down results faster and save time.
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="pt-2 space-y-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={handleClearAll}
            className="w-full h-11 rounded-xl border-gray-200 hover:border-gray-200 bg-white hover:bg-white text-[#A655F8] hover:text-[#A655F8] shadow-none flex items-center justify-center gap-2 text-sm font-semibold transition-none"
          >
            <RefreshCw className="w-4 h-4 text-[#A655F8]" />
            <span>Clear All Filters</span>
          </Button>

          <Button
            type="button"
            onClick={handleApply}
            className="w-full h-11 rounded-xl bg-[#A655F8] hover:bg-[#A655F8] text-white hover:text-white flex items-center justify-between px-4 shadow-none transition-none"
          >
            <Filter className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">Apply Filters</span>
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-white/25 text-xs font-semibold text-white flex items-center justify-center">
              {activeFilterCount}
            </span>
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
  );
};
